import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Coins, TrendingUp, Zap } from 'lucide-react';
// Hooks
import { usePoints } from '../../../hooks/usePoints';
// Components
import { BackButton } from '@/components/BackButton';
import { useProducts } from '@/pages/PointsRedemption/hooks/useProducts';
import { usePendingContentRedirect } from '@/hooks/usePendingContentRedirect';
import { useConfig } from '@/hooks/useConfig';
import { getCardSx } from '@/theme/utils';
import { TYPOGRAPHY } from '@/pages/ListContentPage/constants';

export const Heading = () => {
  const { t } = useTranslation();
  const { points, level, points_to_next_level } = usePoints();
  const config = useConfig();
  const theme = useTheme();
  const products = useProducts();
  const { handlePendingContentRedirect } = usePendingContentRedirect();

  const hasLevel = typeof level === 'number';
  const progressValue =
    hasLevel && points_to_next_level
      ? Math.min(100, (points / (points + points_to_next_level)) * 100)
      : 0;

  return (
    <>
      {/* Back button */}
      <BackButton fallback="/" />

      {/* Points hero card */}
      <Box
        sx={{
          ...getCardSx(theme),
          cursor: 'default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
          gap: 0.5,
        }}
      >
        {config.coin_logo ? (
          <img
            style={{
              width: '48px',
              height: '48px',
              objectFit: 'contain',
              marginBottom: '8px',
            }}
            src={config.coin_logo}
          />
        ) : (
          <Coins
            size={48}
            color={theme.palette.primary.main}
            strokeWidth={1.5}
          />
        )}

        <Typography
          sx={{
            fontSize: '40px',
            fontFamily: TYPOGRAPHY.bold,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.1,
          }}
        >
          {points ?? 0}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: theme.fontSizes.caption,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          {t('pointsScreen.totalPoints')}
        </Typography>

        {/* CTAs */}
        <Box
          display="flex"
          gap={1.5}
          mt={2}
          flexWrap="wrap"
          justifyContent="center"
        >
          {products.hasProducts && (
            <Button
              component={RouterLink}
              to="/points/products?hide_navbar=true"
              variant="contained"
              startIcon={<Zap size={16} />}
              sx={{
                borderRadius: '12px',
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: theme.fontSizes.body,
                px: 3,
              }}
            >
              {t('pointsScreen.redemptionPoints')}
            </Button>
          )}
          <Button
            onClick={handlePendingContentRedirect}
            variant="outlined"
            startIcon={<TrendingUp size={16} />}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontFamily: 'Inter, sans-serif',
              fontSize: theme.fontSizes.body,
              px: 3,
            }}
          >
            {t('pointsScreen.earnPoints')}
          </Button>
        </Box>
      </Box>

      {/* Level progress card */}
      {hasLevel && (
        <Box sx={{ ...getCardSx(theme), cursor: 'default', mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
            }}
          >
            <Chip
              label={`${t('pointsScreen.level')} ${level}`}
              size="small"
              sx={{
                bgcolor: theme.palette.primary.main,
                color: 'white',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: theme.fontSizes.caption,
              }}
            />
            {points_to_next_level && (
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: theme.fontSizes.caption,
                  color: theme.palette.text.secondary,
                }}
              >
                {points_to_next_level} {t('pointsScreen.ptsToNextLevel')}
              </Typography>
            )}
          </Box>

          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: '6px',
              borderRadius: '3px',
              backgroundColor: theme.palette.divider,
              '& .MuiLinearProgress-bar': {
                borderRadius: '3px',
                backgroundColor: theme.palette.primary.main,
              },
            }}
          />

          {points_to_next_level && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: theme.fontSizes.caption,
                  color: theme.palette.text.secondary,
                }}
              >
                {points ?? 0} pts
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: theme.fontSizes.caption,
                  color: theme.palette.text.secondary,
                }}
              >
                {(points ?? 0) + points_to_next_level} pts
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default Heading;
